import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { ProgressCircular } from '@americanexpress/dls-react';
import { addLogToReport, externalRedirect } from '@americanexpress/one-app-ducks';
import useAuthGuard from '../../utils/useAuthGuard';
import { fetchDashboard } from '../../ducks/dashboardApi';
import { getClientContext } from '../../utils/deviceUtils';
import {
  DASHBOARD_ROOT,
  GATEWAY_APP_CONFIG_URL,
  STORE_CONFIG,
  STORE_DASHBOARD,
  STORE_MODULES,
  VALID_ROUTES,
  CONTENT_ROUTES,
  ROUTE_NOT_FOUND,
  REPEATED_USER,
  ROUTE_ERROR,
  ROUTE_TABLE,
  ROUTE_WELCOME,
  ROUTE_ROOT,
} from '../../constants/appConstants';

/** Normalises API response shapes: `{ body: {...} }` or plain object. */
export const normalizeDashboardBody = (response) => response?.body ?? response ?? null;

/** Parses the URL search string into a plain key/value object. */
const resolveQueryParams = (locationSearch = '') => Object.fromEntries(new URLSearchParams(locationSearch));

/** Shared loading spinner used by all guard phases. */
const Spinner = () => (
  <div className="loading-container">
    <ProgressCircular label="Loading" />
  </div>
);

// ── AppRouteGuard ─────────────────────────────────────────────────────────────
function AppRouteGuard({ component: Component, router, location }) {
  const dispatch = useDispatch();
  const hasFetchedRef = useRef(false);

  const pathname = location?.pathname ?? ROUTE_ROOT;

  // ── Selectors ──────────────────────────────────────────────────────────────
  const apiUrl = useSelector((state) => state.getIn([STORE_CONFIG, GATEWAY_APP_CONFIG_URL]));
  const loginUrl = useSelector((state) => state.getIn([STORE_CONFIG, 'loginUrl']));

  const dashboardState = useSelector(
    (state) => state.getIn([STORE_MODULES, DASHBOARD_ROOT, STORE_DASHBOARD])
  );
  const data = dashboardState?.data;
  const isLoading = dashboardState?.isLoading;
  const error = dashboardState?.error;
  const { isAuthChecked } = useAuthGuard();
  // ── Phase 1: Data fetch (entry route only) ─────────────────────────────────
  useEffect(() => {
    if (!isAuthChecked) return;
    if (VALID_ROUTES.has(pathname)) return;

    dispatch(addLogToReport('warn', 'AppRouteGuard – invalid route, redirecting to notFound', {
      moduleID: DASHBOARD_ROOT,
      pathname,
      ...getClientContext(),
    }));
    router.replace(ROUTE_NOT_FOUND);
  }, [isAuthChecked, pathname, router, dispatch]);

  // ── Phase 3: Data fetch (entry route only) ─────────────────────────────────
  useEffect(() => {
    if (!isAuthChecked || pathname !== ROUTE_ROOT) return;
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    const queryParams = resolveQueryParams(location?.search);

    dispatch(addLogToReport('info', 'AppRouteGuard entry route check', {
      moduleID: DASHBOARD_ROOT,
      hasQueryParams: Object.keys(queryParams).length > 0,
      queryParams,
      ...getClientContext(),
    }));

    if (Object.keys(queryParams).length === 0) {
      router.replace(ROUTE_ERROR);
      return;
    }

    dispatch(fetchDashboard({
      apiUrl,
      fetchClient: fetch,
      queryParams,
      loginUrl,
    }));
  }, [isAuthChecked, apiUrl, dispatch, location, loginUrl, pathname, router]);

  // ── Phase 4: API response routing (entry route only) ──────────────────────
  useEffect(() => {
    if (pathname !== ROUTE_ROOT) return;
    if (isLoading) return;
    if (error) {
      dispatch(addLogToReport('warn', 'AppRouteGuard – store error, redirecting to error', {
        moduleID: DASHBOARD_ROOT,
        error,
        ...getClientContext(),
      }));
      router.replace(ROUTE_ERROR);
      return;
    }

    if (!data) return;
    const httpStatus = data.status;

    if (httpStatus === 401) {
      dispatch(addLogToReport('warn', 'AppRouteGuard – 401, redirecting to loginUrl', {
        moduleID: DASHBOARD_ROOT,
        httpStatus,
        ...getClientContext(),
      }));
      dispatch(externalRedirect(loginUrl));
      return;
    }

    if (httpStatus >= 400) {
      dispatch(addLogToReport('warn', 'AppRouteGuard – API error, redirecting to /error', {
        moduleID: DASHBOARD_ROOT,
        httpStatus,
        ...getClientContext(),
      }));
      router.replace(ROUTE_ERROR);
      return;
    }

    const body = normalizeDashboardBody(data);
    const nextView = body?.next_view;

    if (!nextView) {
      dispatch(addLogToReport('warn', 'AppRouteGuard – next_view missing or null, redirecting to error', {
        moduleID: DASHBOARD_ROOT,
        nextView,
        httpStatus,
        ...getClientContext(),
      }));
      router.replace(ROUTE_ERROR);
      return;
    }

    dispatch(addLogToReport('info', 'AppRouteGuard – routing based on next_view', {
      moduleID: DASHBOARD_ROOT,
      nextView,
      httpStatus,
      ...getClientContext(),
    }));
    router.replace(nextView === REPEATED_USER ? ROUTE_TABLE : ROUTE_WELCOME);
  }, [isLoading, data, error, dispatch, loginUrl, pathname, router]);

  // ── Content-route guard: no store data → back to entry ────────────────────
  useEffect(() => {
    if (!isAuthChecked) return;
    if (!CONTENT_ROUTES.has(pathname)) return;
    if (!data) {
      router.replace(ROUTE_ROOT);
    }
  }, [isAuthChecked, data, pathname, router]);

  // ── Render ─────────────────────────────────────────────────────────────────

  // Phase 1 still in flight (or unauthenticated redirect pending)
  if (!isAuthChecked) return <Spinner />;

  // Phase 2: unknown route – redirect in flight
  if (!VALID_ROUTES.has(pathname)) return <Spinner />;

  // Phase 3 + 4: entry route always redirects – never renders a page component
  if (pathname === ROUTE_ROOT) return <Spinner />;

  // Content route without store data – redirect back to / in flight
  if (CONTENT_ROUTES.has(pathname) && !data) return <Spinner />;

  return <Component />;
}

AppRouteGuard.propTypes = {
  component: PropTypes.elementType.isRequired,
  router: PropTypes.shape({ replace: PropTypes.func.isRequired }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }),
};

AppRouteGuard.defaultProps = {
  location: null,
};

// ── withAppGuard HOC ──────────────────────────────────────────────────────────

/**
 * withAppGuard – wraps any page component with AppRouteGuard so it can be
 * passed directly to a `<Route component={...} />` prop.
 *
 * @example
 *   <Route path="/table" component={withAppGuard(TablePage)} />
 */
export const withAppGuard = (Component) => {
  function GuardedRoute({ router, location }) {
    return (
      <AppRouteGuard
        component={Component}
        router={router}
        location={location}
      />
    );
  }

  GuardedRoute.propTypes = {
    router: PropTypes.shape({ replace: PropTypes.func.isRequired }).isRequired,
    location: PropTypes.shape({
      pathname: PropTypes.string,
      search: PropTypes.string,
    }),
  };

  GuardedRoute.defaultProps = {
    location: null,
  };

  GuardedRoute.displayName = `withAppGuard(${
    Component?.displayName || Component?.name || 'Component'
  })`;

  return GuardedRoute;
};

export default AppRouteGuard;
